import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    IconButton,
    Menu,
    MenuItem,
    Box,
    useMediaQuery,
    useTheme,
    Fade,
    Divider,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import {
    Menu as MenuIcon,
    Home as HomeIcon,
    MenuBook as MenuBookIcon,
    Article as ArticleIcon,
    PictureAsPdf as PdfIcon,
    AutoStories as SurahIcon,
    ContactMail as ContactIcon,
    Close as CloseIcon,
} from "@mui/icons-material";
import Logo from "../Logo/Logo";

const navItems = [
    {
        label: "Home",
        to: "/",
        icon: <HomeIcon />,
        description: "AI Islamic companion app",
    },
    {
        label: "Learn Islam",
        to: "/blogs",
        icon: <MenuBookIcon />,
        description: "Quran, Salah, dua & deen guides",
    },
    {
        label: "Quran PDF",
        to: "/quran-pdf-download",
        icon: <PdfIcon />,
        description: "Free Arabic & English Quran PDF",
    },
    {
        label: "Surah Yaseen",
        to: "/surah-yaseen",
        icon: <SurahIcon />,
        description: "Surah Yasin PDF download",
    },
    {
        label: "News",
        to: "/news",
        icon: <ArticleIcon />,
        description: "Islamic & world news",
    },
    {
        label: "Contact",
        to: "/contact",
        icon: <ContactIcon />,
        description: "Get in touch with us",
    },
];

const Header = () => {
    const theme = useTheme();
    const location = useLocation();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [anchorEl, setAnchorEl] = useState(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
        setIsMenuOpen(true);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setIsMenuOpen(false);
    };

    const isActivePath = (path) => location.pathname === path;

    return (
        <AppBar position="sticky" elevation={0}>
            <Toolbar
                sx={{
                    maxWidth: "1200px",
                    width: "100%",
                    mx: "auto",
                    px: { xs: 2, sm: 3, lg: 4 },
                    py: 1,
                }}
            >
                {/* Logo and Brand */}
                <Box
                    sx={{ flexGrow: 1, display: "flex", alignItems: "center" }}
                >
                    <Typography
                        variant="h6"
                        component={Link}
                        to="/"
                        onClick={handleMenuClose}
                        sx={{
                            textDecoration: "none",
                            color: "inherit",
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            fontWeight: 600,
                            fontSize: { xs: "1.1rem", sm: "1.25rem" },
                            "&:hover": {
                                opacity: 0.9,
                            },
                            transition: "opacity 0.2s ease",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 40,
                                height: 40,
                                bgcolor: "rgba(255, 255, 255, 0.1)",
                                borderRadius: "50%",
                                backdropFilter: "blur(10px)",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                    bgcolor: "rgba(255, 255, 255, 0.2)",
                                    transform: "scale(1.05)",
                                },
                            }}
                        >
                            <Logo width={28} height={28} />
                        </Box>
                        <Box>
                            <Box>Quran Chat Buddy</Box>
                        </Box>
                    </Typography>
                </Box>

                {/* Desktop Navigation */}
                {!isMobile && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {navItems.map((item) => (
                            <Button
                                key={item.to}
                                component={item.external ? "a" : Link}
                                {...(item.external
                                    ? { href: item.to }
                                    : { to: item.to })}
                                startIcon={item.icon}
                                sx={{
                                    color: "white",
                                    textTransform: "none",
                                    px: 2,
                                    py: 1,
                                    borderRadius: 2,
                                    fontWeight: 500,
                                    fontSize: "0.875rem",
                                    minWidth: "auto",
                                    transition: "all 0.2s ease",
                                    bgcolor: isActivePath(item.to)
                                        ? "rgba(255, 255, 255, 0.2)"
                                        : "transparent",
                                    "&:hover": {
                                        bgcolor: "rgba(255, 255, 255, 0.1)",
                                        transform: "translateY(-1px)",
                                        boxShadow:
                                            "0 4px 8px rgba(0, 0, 0, 0.2)",
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: { xs: "none", lg: "block" },
                                    }}
                                >
                                    {item.label}
                                </Box>
                                <Box
                                    sx={{
                                        display: { xs: "block", lg: "none" },
                                    }}
                                >
                                    {item.label.split(" ")[0]}
                                </Box>
                            </Button>
                        ))}
                    </Box>
                )}

                {/* Mobile Menu Button */}
                {isMobile && (
                    <IconButton
                        color="inherit"
                        onClick={handleMenuOpen}
                        aria-label="menu"
                        aria-expanded={isMenuOpen}
                        sx={{
                            p: 1,
                            "&:hover": {
                                bgcolor: "rgba(255, 255, 255, 0.1)",
                            },
                        }}
                    >
                        <Box
                            sx={{ position: "relative", width: 24, height: 24 }}
                        >
                            <MenuIcon
                                sx={{
                                    position: "absolute",
                                    transition: "all 0.3s ease",
                                    opacity: isMenuOpen ? 0 : 1,
                                    transform: isMenuOpen
                                        ? "rotate(180deg)"
                                        : "rotate(0deg)",
                                }}
                            />
                            <CloseIcon
                                sx={{
                                    position: "absolute",
                                    transition: "all 0.3s ease",
                                    opacity: isMenuOpen ? 1 : 0,
                                    transform: isMenuOpen
                                        ? "rotate(0deg)"
                                        : "rotate(180deg)",
                                }}
                            />
                        </Box>
                    </IconButton>
                )}

                {/* Mobile Menu */}
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    keepMounted
                    TransitionComponent={Fade}
                    sx={{
                        mt: 1,
                        "& .MuiPaper-root": {
                            borderRadius: 3,
                            minWidth: 280,
                            bgcolor: "rgba(255, 255, 255, 0.98)",
                            backdropFilter: "blur(20px)",
                            border: "1px solid rgba(46, 139, 87, 0.1)",
                            boxShadow: "0 8px 32px rgba(46, 139, 87, 0.15)",
                        },
                        "& .MuiList-root": {
                            p: 1,
                        },
                    }}
                >
                    {/* Menu Header */}
                    <Box
                        sx={{
                            px: 3,
                            py: 2,
                            mb: 1,
                            background:
                                "linear-gradient(135deg, rgba(46, 139, 87, 0.1) 0%, rgba(240, 248, 240, 0.8) 100%)",
                            borderRadius: 2,
                            mx: 1,
                        }}
                    >
                        <Typography
                            variant="subtitle2"
                            align="center"
                            sx={{ color: "text.primary", fontWeight: 600 }}
                        >
                            Navigation Menu
                        </Typography>
                    </Box>

                    {navItems.map((item, index) => (
                        <React.Fragment key={item.to}>
                            <MenuItem
                                component={item.external ? "a" : Link}
                                {...(item.external
                                    ? { href: item.to }
                                    : { to: item.to })}
                                onClick={handleMenuClose}
                                sx={{
                                    borderRadius: 2,
                                    mx: 0.5,
                                    my: 0.5,
                                    px: 2,
                                    py: 1.5,
                                    transition: "all 0.2s ease",
                                    bgcolor: isActivePath(item.to)
                                        ? "rgba(46, 139, 87, 0.1)"
                                        : "transparent",
                                    "&:hover": {
                                        bgcolor: isActivePath(item.to)
                                            ? "rgba(46, 139, 87, 0.15)"
                                            : "rgba(0, 0, 0, 0.04)",
                                        transform: "translateX(4px)",
                                    },
                                }}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 36,
                                        "& .MuiSvgIcon-root": {
                                            fontSize: 20,
                                            color: isActivePath(item.to)
                                                ? "primary.main"
                                                : "text.secondary",
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            width: 32,
                                            height: 32,
                                            borderRadius: 2,
                                            bgcolor: isActivePath(item.to)
                                                ? "primary.light"
                                                : "grey.100",
                                            color: isActivePath(item.to)
                                                ? "primary.contrastText"
                                                : "text.secondary",
                                            transition: "all 0.2s ease",
                                        }}
                                    >
                                        {item.icon}
                                    </Box>
                                </ListItemIcon>
                                <ListItemText
                                    primary={item.label}
                                    secondary={item.description}
                                    primaryTypographyProps={{
                                        variant: "body2",
                                        fontWeight: 500,
                                        color: isActivePath(item.to)
                                            ? "primary.main"
                                            : "text.primary",
                                    }}
                                    secondaryTypographyProps={{
                                        variant: "caption",
                                        color: "text.secondary",
                                    }}
                                />
                            </MenuItem>
                            {index < navItems.length - 1 && (
                                <Divider
                                    sx={{ mx: 2, my: 0.5, opacity: 0.5 }}
                                />
                            )}
                        </React.Fragment>
                    ))}

                    {/* Menu Footer */}
                    <Box
                        sx={{
                            px: 2,
                            py: 1,
                            mt: 1,
                            textAlign: "center",
                            borderTop: "1px solid",
                            borderColor: "grey.200",
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">
                            Built with ❤️ for the Muslim community
                        </Typography>
                    </Box>
                </Menu>
            </Toolbar>

            {/* Mobile Backdrop */}
            {isMobile && isMenuOpen && (
                <Box
                    onClick={handleMenuClose}
                    sx={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: "rgba(0, 0, 0, 0.2)",
                        backdropFilter: "blur(4px)",
                        zIndex: (theme) => theme.zIndex.modal - 1,
                        display: { md: "none" },
                    }}
                />
            )}
        </AppBar>
    );
};

export default Header;
